/*
 * VideoHero — full-bleed looping brand video. No opacity fades: all hero text
 * is rendered at full opacity so it is always readable. The media layer itself
 * (poster + multi-codec video) lives in HeroVideoLayer.
 */
import { useState } from "react";
import { ArrowUpRight, ArrowDown, ChevronDown, CheckCircle2, Pause, Play } from "lucide-react";
import { BOOKING_PATH } from "@/lib/site";
import { useLang } from "@/i18n/LanguageContext";
import { trackClick } from "@/lib/track";
import { HeroDecor } from "./HeroDecor";
import { HeroVideoLayer } from "./HeroVideoLayer";
import { Magnetic } from "@/components/motion/Magnetic";

export const VideoHero = () => {
  const { t } = useLang();
  const [paused, setPaused] = useState(false);
  const h = t.hero;

  return (
    <section id="top" className="relative min-h-screen overflow-hidden flex items-end" data-testid="hero-section">
      {/* Media layer */}
      <div className="absolute inset-0 z-0" data-testid="hero-visual">
        <HeroVideoLayer alt="Legacy Excel configurator rebuilt as a validated system" paused={paused} />
      </div>

      {/* Readability overlays — kept deliberately light so the video motion
          stays visible; the copy gets its own darker left-column wash. */}
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#05070c] via-[#05070c]/30 to-[#05070c]/10" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#05070c]/85 via-[#05070c]/20 to-transparent" />

      {/* Ambient "live engineering system" decor (right side, behind content) */}
      <HeroDecor />

      {/* Content */}
      <div className="relative z-20 w-full mx-auto max-w-7xl px-5 pt-32 pb-20 lg:pb-28">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2.5 glass rounded-full pl-2 pr-4 py-1.5 mb-7" data-testid="hero-pill">
            <span className="rounded-full bg-[rgba(54,226,160,0.16)] text-[var(--green)] font-mono text-[10px] uppercase tracking-widest px-2.5 py-1">
              {h.pillTag}
            </span>
            <span className="text-xs text-white">{h.pillText}</span>
          </div>

          <h1
            className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white"
            data-testid="hero-headline"
          >
            {h.headPre}
            <span className="text-gradient">{h.headHi}</span>
            {h.headPost}
          </h1>

          <p className="mt-6 text-base md:text-lg text-muted leading-relaxed max-w-2xl" data-testid="hero-subheadline">
            {h.sub}
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Magnetic>
              <a href={`${BOOKING_PATH}?src=hero`} onClick={() => trackClick("book_time")} className="btn-primary" data-testid="hero-primary-cta">
                {h.ctaPrimary} <ArrowUpRight size={18} />
              </a>
            </Magnetic>
            <a href="#specialist" className="btn-ghost" data-testid="hero-secondary-cta">
              {h.ctaSecondary} <ArrowDown size={16} />
            </a>
          </div>

          {/* Proof strip */}
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="hero-proof">
            {h.proof.map((pt) => (
              <span key={pt} className="inline-flex items-center gap-2 text-xs text-white/90">
                <CheckCircle2 size={14} className="text-[var(--green)]" /> {pt}
              </span>
            ))}
          </div>

          <p className="mt-7 font-mono text-xs text-[var(--muted-2)] max-w-2xl leading-relaxed" data-testid="hero-credibility">
            {h.credibility}
          </p>
        </div>
      </div>

      {/* Scroll cue */}
      <a
        href="#problem"
        className="absolute z-20 bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-[var(--muted-2)] hover:text-white transition-colors"
        data-testid="hero-scroll-cue"
        aria-label={h.scroll}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]">{h.scroll}</span>
        <ChevronDown size={16} className="animate-bounce" />
      </a>

      {/* WCAG 2.2.2 — explicit control for the auto-playing background loop */}
      <button
        type="button"
        onClick={() => setPaused((p) => !p)}
        aria-label={paused ? h.videoPlay : h.videoPause}
        title={paused ? h.videoPlay : h.videoPause}
        className="absolute z-20 bottom-6 right-6 grid place-items-center h-10 w-10 rounded-full glass text-white/70 hover:text-white hover:scale-105 transition-[color,transform] duration-200"
        data-testid="hero-video-toggle"
      >
        {paused ? <Play size={15} /> : <Pause size={15} />}
      </button>

      <div className="absolute z-20 bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--cyan)] to-transparent opacity-70" />
    </section>
  );
};
